import React, { useEffect, useState } from "react";
import { IconButton, Stack } from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { useFieldArray, Controller, useWatch } from "react-hook-form";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import { CustomComponentCommonProps } from "../utils";

export default function ShareholderFields({
  control,
  isEdit,
  name = "shareholderInfo",
  isLoading,
}) {
  const { fields, append, remove } = useFieldArray({
    control,
    name,
  });

  const shareholderInfo = useWatch({
    control,
    name: name,
  });

  const [hasErrors, setHasErros] = useState(false);
  const totalPercentage = shareholderInfo?.reduce(
    (sum, item) => sum + (Number(item.percentage) || 0),
    0,
  );

  useEffect(() => {
    const hasError =
      shareholderInfo?.some((field) =>
        ["name", "idNo", "percentage"].some((key) => !field[key]?.trim()),
      ) || totalPercentage > 100;

    setHasErros(hasError);
  }, [shareholderInfo, totalPercentage]);

  // Add initial empty shareholder field
  useEffect(() => {
    if (fields.length === 0) {
      append({ name: "", idNo: "", percentage: "" });
    }
  }, []);

  // Calculate total percentage

  // Validate percentage field with total
  const validatePercentage = (value, index) => {
    const otherFieldsTotal = shareholderInfo?.reduce(
      (sum, item, idx) =>
        sum + (idx !== index ? Number(item.percentage) || 0 : 0),
      0,
    );
    const newTotal = Number(value) + otherFieldsTotal;
    if (newTotal > 100) return "Total percentage cannot exceed 100%";
    return true;
  };

  return (
    <Stack direction="column" sx={{ width: "100%", background: "#f9ffff" }}>
      {fields.map((field, index) => (
        <Stack
          key={field.idNo}
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Controller
            name={`${name}.${index}.name`}
            control={control}
            rules={{
              required: true,
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <OutlinedText
                {...field}
                onchangeFn={onChange}
                sxParams={{
                  textError: !!error,
                }}
                divStyle={{ flexGrow: 1 }}
                {...CustomComponentCommonProps(
                  "Shareholder Name",
                  value,
                  "OutlinedText",
                  true,
                  isEdit,
                  isLoading
                )}
                id={`Shareholder Name${index}`}
              />
            )}
          />
          <Controller
            name={`${name}.${index}.idNo`}
            control={control}
            rules={{
              required: true,
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <OutlinedText
                {...field}
                onchangeFn={onChange}
                sxParams={{
                  textError: !!error,
                }}
                divStyle={{ flexGrow: 1 }}
                {...CustomComponentCommonProps(
                  "Shareholder ID Card No./Passport No.",
                  value,
                  "OutlinedText",
                  true,
                  isEdit,
                  isLoading
                )}
                id={`Shareholder ID Card No./Passport No.${index}`}
              />
            )}
          />
          <Controller
            name={`${name}.${index}.percentage`}
            control={control}
            rules={{
              required: true,
              validate: (value) => validatePercentage(value, index),
            }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <OutlinedText
                {...field}
                errorMessage={error?.message || ""}
                onchangeFn={onChange}
                sxParams={{
                  textError: !!error,
                }}
                divStyle={{ flexGrow: 1 }}
                type="number"
                {...CustomComponentCommonProps(
                  "Percentage",
                  value,
                  "OutlinedText",
                  true,
                  isEdit,
                  isLoading
                )}
                id={`Percentage${index}`}
              />
            )}
          />
          <IconButton
            onClick={() => fields.length > 1 && remove(index)}
            sx={{
              color: "error.main",
              height: 40,
              width: 40, // Make it square
              borderRadius: "50%", // Make it circular
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)", // Light gray hover effect
                borderRadius: "50%", // Maintain circular shape on hover
              },
              visibility: fields.length === 1 ? "hidden" : "visible",
            }}
            disabled={!isEdit}
          >
            <Delete fontSize="small" />
          </IconButton>
          <IconButton
            onClick={() => append({ name: "", idNo: "", percentage: "" })}
            variant="outlined"
            size="small"
            sx={{
              height: 40,
              width: 40, // Make it square
              borderRadius: "50%", // Make it circular
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)", // Light gray hover effect
                borderRadius: "50%", // Maintain circular shape on hover
              },
            }}
            disabled={hasErrors || totalPercentage >= 100 || !isEdit} // Disable only if there are real errors
          >
            <Add fontSize="small" />
          </IconButton>
        </Stack>
      ))}
    </Stack>
  );
}
